import React, { useState, useRef, useEffect } from 'react';
import { Box } from '@mui/material';
import { DatagridConfigurable, FunctionField, NumberField, TextField, useListContext } from 'react-admin';
import RaList from '../../components/ra-list/RaList';
import RenderControls, { ControlConfig } from '../../components/ra-list/RenderControls';
import { FilterListProvider } from '../../context/FilterListProvider';
import EventDetailsPopup from './EventDetailsPopup';
import './style.css';

/** Colour palette for drop-severity indicators. */
export const COLORS: Record<string, string> = {
    HighDrop:   '#d32f2f',
    MediumDrop: '#ffa000',
    Stable:     '#388e3c',
};

export const DEFAULT_HOURS = 24;

/** A market-type or similar selectable option. */
interface Choice {
    id: string;
    name: string;
}

/** Props for {@link InventoryDataGrid}. */
interface InventoryDataGridProps {
    /** Pre-selected market type passed in from the overview chart drilldown. */
    initialMarketType?: string;
    /** Available market-type options for the filter control. */
    marketTypeChoices?: Choice[];
}

/** Venue record shape returned by the `InventoryDropAnalysis/Filter` endpoint. */
interface VenueRecord {
    id: string;
    venueId?: number;
    venueName?: string;
    marketType?: string;
    eventCount?: number;
    totalBase?: number;
    totalHAgo?: number;
    total48HAgo?: number;
    total72HAgo?: number;
    totalOneWeekAgo?: number;
    totalTwoWeeksAgo?: number;
    totalMonthAgo?: number;
    [key: string]: unknown;
}

const cellStyle: React.CSSProperties = {
    padding: '6px 16px',
    textAlign: 'right',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
};

/** Running-totals bar rendered below the data grid. */
const DatagridTotals: React.FC = () => {
    const { data } = useListContext<VenueRecord>();
    const ref = useRef<HTMLDivElement>(null);

    const sum = (f: keyof VenueRecord) =>
        (data || []).reduce((s, r) => s + ((r[f] as number) || 0), 0);

    const totals = {
        events: sum('eventCount'),
        base:   sum('totalBase'),
        hAgo:   sum('totalHAgo'),
        _48hAgo: sum('total48HAgo'),
        _72hAgo: sum('total72HAgo'),
        d7:  sum('totalOneWeekAgo'),
        d14: sum('totalTwoWeeksAgo'),
        m1:  sum('totalMonthAgo'),
    };

    useEffect(() => {
        const headerCells = document.querySelectorAll<HTMLElement>('.RaDatagrid-headerCell');
        if (!headerCells.length || !ref.current) return;
        const cols = Array.from(headerCells).map((c) => `${c.offsetWidth}px`).join(' ');
        ref.current.style.gridTemplateColumns = cols;
    }, [data]);

    if (!data?.length) return null;

    return (
        <Box
            ref={ref}
            sx={{
                display: 'grid',
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: 'action.hover',
                alignItems: 'center',
            }}
        >
            <span style={{ padding: '6px 16px' }}><b>Total</b></span>
            <span style={cellStyle} />
            <span style={cellStyle}>{totals.events.toLocaleString()}</span>
            <span style={cellStyle}>
                {renderDropCell(totals.hAgo, totals.base)}
            </span>
            <span style={cellStyle}>{totals._48hAgo.toLocaleString()}</span>
            <span style={cellStyle}>{totals._72hAgo.toLocaleString()}</span>
            <span style={cellStyle}>{totals.d7.toLocaleString()}</span>
            <span style={cellStyle}>{totals.d14.toLocaleString()}</span>
            <span style={cellStyle}>{totals.m1.toLocaleString()}</span>
        </Box>
    );
};

/**
 * Renders a current-inventory cell with an inline drop indicator.
 *
 * @param value - Current inventory value.
 * @param base  - Baseline inventory value (H hours ago).
 * @returns JSX element showing the value and percentage drop, or `null` when
 *          either argument is `null` / `undefined`.
 */
export const renderDropCell = (
    value: number | null | undefined,
    base:  number | null | undefined,
): React.ReactNode => {
    const drop = ((base  ?? 0) - (value ?? 0));
    const pct  = (base ?? 0) > 0 ? ((drop / (base as number)) * 100).toFixed(1) : null;

    if (value === null || value === undefined || base === null || base === undefined) return null;

    return (
        <div style={{ lineHeight: '1.2em', textAlign: 'right' }}>
            <span style={{ fontWeight: 500 }}>{value?.toLocaleString() ?? 0}</span>
            <br />
            <span style={{ color: COLORS.HighDrop, fontSize: '0.78em' }}>
                {` Drop: ${drop.toLocaleString()} ${pct !== null && `(↓${pct}%)`}`}
            </span>
        </div>
    );
};

/**
 * Venue-level data grid for the Inventory Drop Analysis page.
 *
 * Uses `RaList` + `RenderControls` for filtering.  Clicking a row opens
 * {@link EventDetailsPopup} which fetches per-event data lazily.
 *
 * @param props - {@link InventoryDataGridProps}
 */
const InventoryDataGrid: React.FC<InventoryDataGridProps> = ({ initialMarketType, marketTypeChoices }) => {
    const refbtnFilter = useRef<HTMLButtonElement>(null);
    const [popupVenue, setPopupVenue]     = useState<VenueRecord | null>(null);
    const [displayHours, setDisplayHours] = useState<number>(DEFAULT_HOURS);
    const [hours, setHours]               = useState<number>(DEFAULT_HOURS);
    const [defaultValues] = useState({
        marketType_eq: initialMarketType || '',
        venueName:     '',
        hours:         DEFAULT_HOURS,
    });
    const [ready, setReady]     = useState<boolean>(false);
    const [isLoaded, setIsLoaded] = useState<boolean>(false);

    useEffect(() => { setReady(true); }, []);
    useEffect(() => {
        if (ready && refbtnFilter.current) refbtnFilter.current.click();
    }, [ready]);

    const controls: ControlConfig[] = [
        {
            component: 'select',
            name:      'marketType_eq',
            label:     'Market Type',
            choices:   marketTypeChoices || [],
            xs:        2.5,
        },
        {
            component: 'number',
            name:      'venueId_eq',
            label:     'VenueId',
            xs:        1.5,
        },
        {
            component: 'text',
            name:      'venueName',
            label:     'Venue Name',
            xs:        2,
        },
        {
            component: 'number',
            name:      'hours',
            label:     `Hours (drop window, default ${DEFAULT_HOURS})`,
            xs:        2,
            onChange:  ({ value }) =>
                setHours(parseInt(value as string, 10) || DEFAULT_HOURS),
        },
    ];

    const handleRowClick = (_id: unknown, _resource: unknown, record: VenueRecord) => {
        setPopupVenue(record);
    };

    return (
        <>
            <RaList
                title="Venue-Level Inventory"
                sort={{ field: 'eventCount', order: 'DESC' }}
                perPage={200}
                pagination={[200, 500, 1000, 5000]}
                filterDefaultValues={defaultValues}
                filters={
                    <FilterListProvider>
                        <RenderControls
                            refFilter={refbtnFilter}
                            controls={controls}
                            defaultValues={defaultValues}
                            exportFile={{
                                columns: [
                                    'MarketType', 'VenueId', 'VenueName', 'EventCount',
                                    'TotalHAgo', 'Total48HAgo', 'Total72HAgo',
                                    'TotalOneWeekAgo', 'TotalTwoWeeksAgo', 'TotalMonthAgo',
                                ],
                            }}
                        />
                    </FilterListProvider>
                }
                queryOptions={{
                    onSuccess: () => {
                        setDisplayHours(hours);
                        setIsLoaded(true);
                    },
                }}
            >
                <>
                    <DatagridConfigurable
                        key={displayHours}
                        bulkActionButtons={false}
                        rowClick={handleRowClick as never}
                    >
                        <TextField
                            source="venueName"
                            label="Venue Name"
                            sx={{
                                width: 100,
                                maxWidth: 100,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        />
                        <TextField source="venueId"             label="VenueId" />
                        <NumberField source="eventCount"       label="Total Dropped Events" />
                        <FunctionField
                            source="totalHAgo"
                            label={`Current (${displayHours}h Ago)`}
                            headerClassName="drop-window-right-header"
                            sx={{ textAlign: 'right' }}
                            render={(record: VenueRecord) =>
                                renderDropCell(record.totalHAgo, record.totalBase)
                            }
                        />
                        <NumberField source="total48HAgo"       label="48h Ago" />
                        <NumberField source="total72HAgo"       label="72h Ago" />
                        <NumberField source="totalOneWeekAgo"   label="7 Days Ago" />
                        <NumberField source="totalTwoWeeksAgo"  label="14 Days Ago" />
                        <NumberField source="totalMonthAgo"     label="1 Month Ago" />
                    </DatagridConfigurable>
                    {isLoaded && <DatagridTotals />}
                </>
            </RaList>

            <EventDetailsPopup
                open={Boolean(popupVenue)}
                onClose={() => setPopupVenue(null)}
                venue={popupVenue}
                hours={displayHours}
            />
        </>
    );
};

export default InventoryDataGrid;
